import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, PopoverShell, TagGeneral, Text, sparkToast } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { getUserFacingDatasetName } from '@/features/datasets/utils/datasetCloneNames';
import {
    displayAiGeneratedDatasetName,
    isAiGeneratedDatasetName,
} from '@/features/datasets/utils/aiGeneratedDatasetDefaults';
import { queuedWaitingSubtitle } from '@/features/datasets/utils/retrievalAgentProgress';
import { disarmAutoTrain, listActiveDataFlowsForUsecase } from '@/features/trainings/utils/autoTrain';
import { resolveTriggeringPhase } from '@/features/trainings/utils/autoTrainPending';
import ActiveProgressCard from './ActiveProgressCard';

/** "data_searching" → "Data searching" (same humanizer as the training tag). */
const statusLabel = (status) => {
    const label = String(status || '').split('_').join(' ').toLowerCase();
    return label ? label.charAt(0).toUpperCase() + label.slice(1) : '';
};

/** User-visible dataset title (drop clone suffixes / AI-generated prefixes). */
const flowDisplayName = (datasetName) => {
    if (!datasetName) return 'Data flow';
    const userFacing = getUserFacingDatasetName(datasetName) || datasetName;
    if (isAiGeneratedDatasetName(userFacing)) {
        return displayAiGeneratedDatasetName(userFacing);
    }
    return userFacing;
};

const flowStatusLabel = (flow) => {
    if (flow.flowType === 'generation') {
        if (flow.isQueued) return queuedWaitingSubtitle(flow.jobsAhead);
        if (flow.generationPhase === 'checking_labels' || flow.phase === 'checking_labels') {
            return 'Checking labels';
        }
        const progress = Number(flow.progress);
        if (Number.isFinite(progress) && progress > 0) {
            return `${Math.max(0, Math.min(100, Math.round(progress)))}%`;
        }
        return 'Generating';
    }
    if (flow.flowType === 'upload') {
        if (flow.phase === 'uploading') return 'Uploading';
        if (flow.phase === 'label_generation') return 'Generating labels';
        const progress = Number(flow.progress);
        if (Number.isFinite(progress) && progress > 0) {
            return `${Math.max(0, Math.min(100, Math.round(progress)))}%`;
        }
        return 'Processing';
    }
    if (flow.status === 'queued') return queuedWaitingSubtitle(flow.jobsAhead);
    return statusLabel(flow.status || flow.phase) || 'Running';
};

/** Reconciliation-poll cadence while the backend settle thread is "triggering". */
export const TRIGGERING_POLL_INTERVAL_MS = 30_000;

/**
 * Consecutive reconciliation polls with an unchanged settle heartbeat before
 * the card reports a stall. The backend settle thread refreshes
 * `pending.settle.heartbeat` every few seconds, so 3 silent polls (~90 s)
 * means the thread died with its pod (the backend sweep reclaims it; the
 * card reports the stall meanwhile).
 */
export const STALLED_HEARTBEAT_POLLS = 3;

/**
 * Elapsed triggering time after which the subtitle warns that the settle wait
 * is taking longer than expected (mirror of the backend's
 * SETTLE_STALL_MAX_SECONDS stall deadline).
 */
export const TRIGGERING_LONG_WAIT_MS = 15 * 60_000;

/**
 * Docked bottom-right card for a scheduled auto-train (same shell / Figma
 * chrome as the training card): lists the task's running Generate-with-AI /
 * Retrieval Agent / dataset & benchmark upload flows and starts training
 * automatically when they finish. The trash control opens a cancel-auto-train
 * confirm.
 */
const AutoTrainProgressBanner = ({ usecase }) => {
    const { progressStatus, updateUsecase, refreshUsecases } = useContext(GlobalContext);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [disarming, setDisarming] = useState(false);
    const closeAreaRef = useRef(null);

    useEffect(() => {
        if (!confirmOpen) return undefined;
        const onMouseDown = (event) => {
            if (!closeAreaRef.current?.contains(event.target)) {
                setConfirmOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setConfirmOpen(false);
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [confirmOpen]);

    const pending = usecase?.auto_train_pending;
    const triggering = Boolean(pending?.triggering);

    // Latest-value ref so the poll interval below can read the freshest
    // heartbeat without re-subscribing on every refetch.
    const settleHeartbeat = pending?.settle?.heartbeat || null;
    const latestHeartbeatRef = useRef(settleHeartbeat);
    latestHeartbeatRef.current = settleHeartbeat;
    const [stalled, setStalled] = useState(false);

    // While the backend settle thread holds the "triggering" claim the outcome
    // normally arrives over the WebSocket (updateAutoTrainStatus). If that
    // message is missed (reconnect, pod restart mid-settle) the card would say
    // "Starting" forever — poll the usecase list until the pending clears.
    // The same poll doubles as the stall detector: the settle thread refreshes
    // `pending.settle.heartbeat` every few seconds, so a heartbeat that stays
    // identical across STALLED_HEARTBEAT_POLLS polls means the thread died.
    // Comparing heartbeat *values* across polls (instead of against the local
    // clock) makes the detection immune to client/server clock skew.
    useEffect(() => {
        if (!triggering) {
            setStalled(false);
            return undefined;
        }
        let lastSeen = latestHeartbeatRef.current;
        let unchangedPolls = 0;
        const interval = setInterval(() => {
            refreshUsecases?.();
            const current = latestHeartbeatRef.current;
            if (current !== lastSeen) {
                lastSeen = current;
                unchangedPolls = 0;
                setStalled(false);
            } else {
                unchangedPolls += 1;
                if (unchangedPolls >= STALLED_HEARTBEAT_POLLS) setStalled(true);
            }
        }, TRIGGERING_POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [triggering, refreshUsecases]);

    if (!pending) return null;

    const flows = listActiveDataFlowsForUsecase(progressStatus, usecase._id);

    const handleConfirmDisarm = () => {
        if (disarming) return;
        setDisarming(true);
        disarmAutoTrain({
            usecase,
            onSuccess: () => {
                updateUsecase?.(usecase._id, { auto_train_pending: null });
                sparkToast.info('Automatic training canceled');
                setDisarming(false);
            },
            onError: (message) => {
                sparkToast.error(message);
                setDisarming(false);
            },
        });
    };

    const triggeringPhase = resolveTriggeringPhase(pending);

    // Elapsed settle time — display-only (client/server clock skew merely
    // shifts the label; the stall detection above never uses wall clocks).
    const elapsedMs = (() => {
        if (!triggering || !pending?.triggering_since) return null;
        const startedAt = Date.parse(pending.triggering_since);
        if (Number.isNaN(startedAt)) return null;
        return Math.max(0, Date.now() - startedAt);
    })();
    const longWait = elapsedMs !== null && elapsedMs >= TRIGGERING_LONG_WAIT_MS;
    const elapsedLabel = elapsedMs !== null && elapsedMs >= 60_000 ? `${Math.floor(elapsedMs / 60_000)}m` : null;

    const triggeringSubtitle = (() => {
        if (stalled) {
            return 'Training start stalled - it retries automatically. You can also cancel and train manually.';
        }
        const base = longWait
            ? 'Taking longer than expected - still waiting for the data to finish processing'
            : triggeringPhase?.phase === 'starting'
                ? 'Data is ready - starting training…'
                : 'Data flows finished - processing data before training…';
        return elapsedLabel ? `${base} · ${elapsedLabel}` : base;
    })();

    const tagLabel = triggering
        ? stalled
            ? 'Stalled'
            : triggeringPhase?.phase === 'starting'
                ? 'Starting training'
                : 'Finalizing data'
        : flows.length > 0
            ? `${flows.length} ${flows.length === 1 ? 'flow' : 'flows'} running`
            : 'Waiting';

    return (
        <ActiveProgressCard
            iconSlug="data"
            title="Waiting for data to train"
            tag={<TagGeneral label={tagLabel} />}
            percent={null}
            deleteAriaLabel="Cancel automatic training"
            confirmOpen={confirmOpen}
            onDeleteClick={() => setConfirmOpen((open) => !open)}
            closeAreaRef={closeAreaRef}
            confirmDialog={(
                <div
                    className="training-progress-banner__confirm"
                    role="dialog"
                    aria-label="Cancel automatic training"
                >
                    <PopoverShell
                        tip
                        tipDirection="bottom"
                        tipAlign="end"
                        className="training-progress-banner__confirm-shell"
                        panelClassName="training-progress-banner__confirm-panel"
                    >
                        <div className="training-progress-banner__confirm-body">
                            <Text variant="base-semibold" as="p">
                                Cancel automatic training
                            </Text>
                            <Text variant="base-regular" color="secondary" as="p">
                                Training will no longer start by itself. The running data flows keep going.
                            </Text>
                        </div>
                        <div className="training-progress-banner__confirm-actions">
                            <Button
                                variant="tertiary"
                                size="small"
                                type="button"
                                onClick={() => setConfirmOpen(false)}
                            >
                                Keep
                            </Button>
                            <Button
                                variant="primary"
                                size="small"
                                danger
                                type="button"
                                disabled={disarming}
                                disableWhenReadOnly
                                onClick={() => {
                                    setConfirmOpen(false);
                                    handleConfirmDisarm();
                                }}
                            >
                                Cancel auto-train
                            </Button>
                        </div>
                    </PopoverShell>
                </div>
            )}
        >
            <div className="auto-train-banner__flows">
                {triggering ? (
                    <>
                        {(triggeringPhase?.datasets || []).map((dataset) => (
                            <div
                                key={`${dataset.name}:${dataset.is_benchmark ? 'benchmark' : 'dataset'}`}
                                className="auto-train-banner__flow-row"
                            >
                                <span className="auto-train-banner__flow-name">
                                    <Text variant="detail-regular">
                                        {flowDisplayName(dataset.name)}
                                    </Text>
                                </span>
                                <span className="auto-train-banner__flow-status">
                                    <Text variant="detail-regular" color="secondary">
                                        {`${Number(dataset.records || 0).toLocaleString()} records`}
                                    </Text>
                                </span>
                            </div>
                        ))}
                        <Text variant="detail-regular" color="secondary">
                            {triggeringSubtitle}
                        </Text>
                    </>
                ) : flows.length > 0 ? (
                    flows.map((flow) => (
                        <div
                            key={flow.progressKey}
                            className="auto-train-banner__flow-row"
                        >
                            <span className="auto-train-banner__flow-name">
                                <Text variant="detail-regular">
                                    {flowDisplayName(flow.datasetName)}
                                </Text>
                            </span>
                            <span className="auto-train-banner__flow-status">
                                <Text variant="detail-regular" color="secondary">
                                    {flowStatusLabel(flow)}
                                </Text>
                            </span>
                        </div>
                    ))
                ) : (
                    <Text variant="detail-regular" color="secondary">
                        Waiting for the data flows to finish, then training starts automatically
                    </Text>
                )}
            </div>
        </ActiveProgressCard>
    );
};

export default AutoTrainProgressBanner;
